/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import { LoadingSpinner } from '@/components';
import { useAxios } from '@/components/AxiosContext';
import { SimpleAlert } from '@/components/SimpleAlert/SimpleAlert';

import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import { exportWorkplan } from '@/pdf';
import {
    Button,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
} from '@chakra-ui/react';
import React, { forwardRef, Ref, useImperativeHandle, useState } from 'react';
import { Assignment } from '@/wpt-lib/interfaces/Assignment';

export interface ExportWorkplanRef {
    export(workplan: Workplan, version: number, requestorAssignment: Assignment): void;
}

const ExportWorkplan = forwardRef<ExportWorkplanRef, { ref: Ref<ExportWorkplanRef> }>(function ExportWorkplan(
    props,
    ref,
) {
    const axios = useAxios().axiosInstance;

    const [processing, setProcessing] = useState(false);
    const [failed, setFailed] = useState(false);

    const { isOpen, onClose: closeModal, onOpen: openModal } = useDisclosure();

    useImperativeHandle(ref, () => ({
        export: (workplan: Workplan, version: number, requestorAssignment: Assignment) => {
            openModal();

            setProcessing(true);
            setFailed(false);

            setTimeout(async () => {
                // Defer export so the modal overlay has enough time to display
                // before the heavy blocking export computation occurs.
                try {
                    if (workplan.assignment) {
                        await exportWorkplan(workplan, version, axios, requestorAssignment);
                        closeModal();
                    } else {
                        console.log('Workplan assignment not loaded.');
                        setFailed(true);
                    }
                } catch (e) {
                    console.log('Generating failed, ', e);
                    setFailed(true);
                }

                setProcessing(false);
            }, 0);
        },
    }));

    const handleClose = () => {
        if (!processing) {
            closeModal();
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <ModalOverlay />
            <ModalContent aria-label="Exporting workplan...">
                <ModalHeader>Exporting workplan...</ModalHeader>
                <ModalBody paddingBottom={processing ? 10 : 0}>
                    {processing && <LoadingSpinner />}
                    {failed && (
                        <SimpleAlert variant="subtle" status="error">
                            PDF export failed
                        </SimpleAlert>
                    )}
                </ModalBody>
                {failed && (
                    <ModalFooter>
                        <Button onClick={closeModal}>Close</Button>
                    </ModalFooter>
                )}
            </ModalContent>
        </Modal>
    );
});

export default ExportWorkplan;
